import React, { useState, useEffect, useRef } from "react";
import { LayoutDashboard, Shield, ShieldAlert, CalendarCheck, ClipboardList,
  Monitor, CheckSquare, AlertTriangle, Link2, Settings, Lock, GitMerge,
  Users, Building2, FileText, Bot, RefreshCw, Upload, Download,
  AlertCircle, Bell, Key, Activity, Eye, Trash2, Edit2, Plus,
  ChevronDown, ChevronUp, Search, Filter, X, Check, Info,
  BarChart2, PieChart, TrendingUp, Clock, Zap, Database,
  Server, Cloud, Package, Code, Globe, Cpu, HardDrive, Network } from "lucide-react";

// Lucide icon helper — renders icon with consistent sizing
var Icon = function(props) {
  var name = props.name;
  var size = props.size || 15;
  var color = props.color || "currentColor";
  var style = props.style || {};
  var icons = {
    LayoutDashboard, Shield, ShieldAlert, CalendarCheck, ClipboardList,
    Monitor, CheckSquare, AlertTriangle, Link2, Settings, Lock, GitMerge,
    Users, Building2, FileText, Bot, RefreshCw, Upload, Download,
    AlertCircle, Bell, Key, Activity, Eye, Trash2, Edit2, Plus,
    BarChart2, PieChart, TrendingUp, Clock, Zap, Database,
    Server, Cloud, Package, Code, Globe, Cpu, HardDrive, Network
  };
  var Comp = icons[name];
  if (!Comp) return React.createElement("span", {style:{width:size,height:size,display:"inline-block"}});
  return React.createElement(Comp, {size:size, color:color, style:Object.assign({flexShrink:0,display:"block"},style)});
};

// ── Environment ───────────────────────────────────────────────────────────────
// Demo mode permanently disabled — production only
var API_BASE = process.env.REACT_APP_API_URL || "/api";
var APP_NAME = process.env.REACT_APP_NAME || "AVTA";

// ── Themes ────────────────────────────────────────────────────────────────────
var THEMES = {
  dark: {
    bg:"#0b0f1a", sidebar:"#111827", card:"#111827", border:"#1e2d45",
    text:"#e2e8f0", text2:"#8899b4", text3:"#4d6280",
    accent:"#3b82f6", accentBg:"rgba(59,130,246,0.1)", inset:"#1a2233",
    success:"#10b981", warning:"#f59e0b", danger:"#ef4444", info:"#06b6d4",
    shadow:"rgba(0,0,0,0.5)",
    cardShadow:"0 1px 3px rgba(0,0,0,0.4), 0 1px 2px rgba(0,0,0,0.3)",
    pill:"#2a3f5f",
    border2:"#2a3f5f", s2:"#1a2233", s3:"#212d42",
  },
  light: {
    bg:"#f8fafc", sidebar:"#ffffff", card:"#ffffff", border:"#cbd5e1",
    text:"#0f172a", text2:"#475569", text3:"#94a3b8",
    accent:"#2563eb", accentBg:"rgba(37,99,235,0.08)", inset:"#f1f5f9",
    success:"#059669", warning:"#d97706", danger:"#dc2626", info:"#0891b2",
    shadow:"rgba(15,23,42,0.06)",
    cardShadow:"0 1px 3px rgba(15,23,42,0.08), 0 1px 2px rgba(15,23,42,0.04)",
    pill:"#e2e8f0",
    border2:"#94a3b8", s2:"#f1f5f9", s3:"#e2e8f0",
  },
};
export var ThemeCtx = React.createContext({ T: THEMES.dark, name:"dark", toggle:function(){} });

// ── Colour maps ───────────────────────────────────────────────────────────────
export var SEV_C  = { Critical:"#e5534b", High:"#e8892b", Medium:"#c69026", Low:"#2ea043" };
export var STAT_C = { Open:"#388bfd","In Progress":"#58a6ff",Remediated:"#2ea043",Exception:"#8957e5",Overdue:"#d29922","SLA Breach":"#e5534b" };

// ── API client ────────────────────────────────────────────────────────────────
var _at = null;
var _rt = localStorage.getItem("avta_refresh") || null;
export var api = {
  setTokens: function(a,r){ _at=a; _rt=r; if(r) localStorage.setItem("avta_refresh",r); },
  clearTokens: function(){ _at=null; _rt=null; localStorage.removeItem("avta_refresh"); },
  request: async function(path, opts){
    opts = opts||{};
    var h = Object.assign({"Content-Type":"application/json"}, opts.headers||{});
    if(_at) h["Authorization"]="Bearer "+_at;
    var res = await fetch(API_BASE+path, Object.assign({},opts,{headers:h}));
    if(res.status===401 && _rt){
      var r = await fetch(API_BASE+"/auth/refresh",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({refresh_token:_rt})});
      if(r.ok){ var d=await r.json(); api.setTokens(d.access_token,d.refresh_token); h["Authorization"]="Bearer "+_at; res=await fetch(API_BASE+path,Object.assign({},opts,{headers:h})); }
      else { api.clearTokens(); window.location.reload(); return null; }
    }
    if(opts._raw) return res;
    if(!res.ok){ var e=await res.json().catch(function(){return{error:res.statusText};}); throw new Error(e.error||res.statusText); }
    return res.status===204?null:res.json();
  },
  get:    function(p){   return api.request(p); },
  post:   function(p,b){ return api.request(p,{method:"POST",  body:JSON.stringify(b)}); },
  patch:  function(p,b){ return api.request(p,{method:"PATCH", body:JSON.stringify(b)}); },
  delete: function(p){   return api.request(p,{method:"DELETE"}); },
  put:    function(p,b){ return api.request(p,{method:"PUT",   body:JSON.stringify(b)}); },
  hasRefreshToken: function(){ return !!_rt; },
  getRefreshToken: function(){ return _rt; },
  _getToken:       function(){ return _at; },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
export function Av(p){ var s=p.s||30; return React.createElement("div",{style:{width:s,height:s,borderRadius:"50%",background:p.c||"#6366f1",display:"flex",alignItems:"center",justifyContent:"center",fontSize:s*.34,fontWeight:700,color:"#fff",flexShrink:0}},p.i); }
export function Bdg(p){ return React.createElement("span",{style:{background:p.c+"18",color:p.c,border:"1px solid "+p.c+"30",borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:600,whiteSpace:"nowrap",letterSpacing:0.2}},p.t); }
export function Dot(p){ return React.createElement("span",{style:{width:7,height:7,borderRadius:"50%",background:p.c,display:"inline-block",marginRight:5,flexShrink:0}}); }
export var PAGE_SIZE=50;
export function usePagination(data,size){
  size=size||PAGE_SIZE;
  var _s=React.useState(1); var pg=_s[0]; var setPg=_s[1];
  var total=data?data.length:0;
  var pages=Math.max(1,Math.ceil(total/size));
  React.useEffect(function(){setPg(1);},[total]);
  var safePg=Math.min(pg,pages);
  var slice=data?data.slice((safePg-1)*size,safePg*size):[];
  return {pg:safePg,setPg:setPg,pages:pages,slice:slice,total:total};
}
export function Pager(p){
  var T=React.useContext(ThemeCtx).T;
  if(!p||!p.pages||p.pages<=1) return null;
  var btns=[["<<",1],["<",p.pg-1],[">",p.pg+1],[">>",p.pages]];
  return React.createElement("div",{style:{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"9px 14px",borderTop:"1px solid "+T.border}},
    React.createElement("span",{style:{fontSize:11,color:T.text3}},p.total+" total - page "+p.pg+" of "+p.pages),
    React.createElement("div",{style:{display:"flex",gap:4}},
      btns.map(function(b){
        var disabled=(b[0]==="<<"||b[0]==="<")?p.pg<=1:(p.pg>=p.pages);
        return React.createElement("button",{key:b[0],onClick:function(){p.setPg(Math.max(1,Math.min(p.pages,b[1])));},disabled:disabled,
          style:{padding:"3px 9px",background:T.inset,border:"1px solid "+T.border,borderRadius:5,
            cursor:disabled?"default":"pointer",color:T.text2,fontSize:12,opacity:disabled?0.35:1}},b[0]);
      })
    )
  );
}
export function Card(p){ var T=React.useContext(ThemeCtx).T; var _s=p.style; var _c=p.children; var rest=Object.assign({},p); delete rest.style; delete rest.children; return React.createElement("div",Object.assign({},rest,{style:Object.assign({background:T.card,border:"0.5px solid "+T.border,borderRadius:8,padding:"12px 14px",boxShadow:T.cardShadow||"0 1px 3px rgba(0,0,0,0.1)"},_s||{})}),_c); }
export function AdminBadge(){ return React.createElement("span",{style:{fontSize:11,color:"#ef4444",background:"#ef444422",border:"1px solid #ef444444",borderRadius:4,padding:"2px 7px",fontWeight:600,marginLeft:8}},"Admin only"); }
export function Modal(p){
  var T=React.useContext(ThemeCtx).T;
  return React.createElement("div",{style:{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:16}},
    React.createElement("div",{style:{background:T.card,border:"1px solid "+T.border,borderRadius:16,width:"100%",maxWidth:p.width||500,maxHeight:"90vh",overflowY:"auto",boxShadow:"0 16px 48px rgba(0,0,0,0.2),0 4px 16px rgba(0,0,0,0.1)"}},
      React.createElement("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"16px 20px",borderBottom:"1px solid "+T.border}},
        React.createElement("div",{style:{fontSize:15,fontWeight:700,color:T.text}},p.title),
        React.createElement("button",{onClick:p.onClose,style:{background:"transparent",border:"none",color:T.text3,cursor:"pointer",fontSize:22,lineHeight:1}},"\u00d7")
      ),
      React.createElement("div",{style:{padding:"16px 20px"}},p.children)
    )
  );
}


export function ToastProvider({children}){
  var _t=useState([]); var toasts=_t[0]; var setToasts=_t[1];
  var addToast=function(msg,type,duration){
    var id=++_toastId;
    setToasts(function(p){return p.concat([{id,msg,type:type||"info"}]);});
    setTimeout(function(){setToasts(function(p){return p.filter(function(t){return t.id!==id;});});}, duration||3500);
    return id;
  };
  var removeToast=function(id){setToasts(function(p){return p.filter(function(t){return t.id!==id;});});};
  return React.createElement(ToastCtx.Provider,{value:{toasts,addToast,removeToast}},
    children,
    React.createElement(ToastContainer,{toasts:toasts,remove:removeToast})
  );
}


export function ToastContainer({toasts,remove}){
  if(!toasts.length) return null;
  var colors={success:"#22c55e",error:"#ef4444",warning:"#f59e0b",info:"#A100FF"};
  var icons={success:"OK",error:"!",warning:"!",info:"i"};
  return React.createElement("div",{style:{position:"fixed",bottom:24,right:24,zIndex:9999,display:"flex",flexDirection:"column",gap:8,maxWidth:360}},
    toasts.map(function(t){
      var col=colors[t.type]||colors.info;
      return React.createElement("div",{key:t.id,
        style:{background:"#fff",border:"1px solid "+col+"44",borderLeft:"4px solid "+col,
          padding:"12px 14px",boxShadow:"0 4px 16px rgba(0,0,0,0.12)",
          display:"flex",alignItems:"center",gap:10,minWidth:240,animation:"slideIn .2s ease"}},
        React.createElement("div",{style:{width:20,height:20,borderRadius:"50%",background:col,
          color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",
          fontSize:11,fontWeight:700,flexShrink:0}},icons[t.type]||"i"),
        React.createElement("div",{style:{flex:1,fontSize:13,color:"#1a1a1a",lineHeight:1.4}},t.msg),
        React.createElement("button",{onClick:function(){remove(t.id);},
          style:{background:"transparent",border:"none",color:"#94a3b8",cursor:"pointer",fontSize:16,lineHeight:1,padding:"0 2px"}},"x")
      );
    })
  );
}


export function useToast(){return React.useContext(ToastCtx);}



export function ConfirmDialog({msg,subMsg,confirmLabel,confirmColor,onConfirm,onCancel}){
  var T=React.useContext(ThemeCtx).T;
  return React.createElement("div",{style:{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",
    display:"flex",alignItems:"center",justifyContent:"center",zIndex:300,padding:16}},
    React.createElement("div",{style:{background:T.card,border:"1px solid "+T.border,
      borderRadius:12,padding:"24px 28px",maxWidth:400,width:"100%",
      boxShadow:"0 20px 60px rgba(0,0,0,0.3)"}},
      React.createElement("div",{style:{fontSize:15,fontWeight:700,color:T.text,marginBottom:subMsg?6:16}},msg),
      subMsg&&React.createElement("div",{style:{fontSize:13,color:T.text2,marginBottom:16,lineHeight:1.6}},subMsg),
      React.createElement("div",{style:{display:"flex",gap:8,justifyContent:"flex-end"}},
        React.createElement("button",{onClick:onCancel,
          style:{background:"transparent",border:"1px solid "+T.border,color:T.text2,
            padding:"8px 18px",cursor:"pointer",fontSize:13,borderRadius:6}},"Cancel"),
        React.createElement("button",{onClick:onConfirm,
          style:{background:confirmColor||"#A100FF",color:"#fff",border:"none",
            padding:"8px 20px",cursor:"pointer",fontSize:13,fontWeight:700,borderRadius:6}},
          confirmLabel||"Confirm")
      )
    )
  );
}

